import {PropsWithChildren} from "react";
import {GoogleMap, GoogleMapProps, useJsApiLoader} from "@react-google-maps/api";
import {GeoCoord, Locatable, LocatableEntity, LocationContext} from "@ots-frontend/ots-data";
import {OpsLocationStatus} from "../../../model/ops-status";
import {UseLoadScriptOptions} from "@react-google-maps/api/dist/useLoadScript";
import {Libraries} from "@react-google-maps/api/dist/utils/make-load-script-url";

export type MapState = {
  bounds: google.maps.LatLngBoundsLiteral | undefined;
};

export interface MapFocusPoint extends Locatable {
  id: string;
  displayName: string;
  latLng?: GeoCoord
}

export type MapDetail =
  | { mode: "entity"; context: LocatableEntity }
  | { mode: "context"; context: LocationContext }
  | { mode: "ops"; context: OpsLocationStatus }

export const isEntityMapDetail = (d: MapDetail): d is { mode: "entity"; context: LocatableEntity } =>
  d.mode === "entity";

export const isContextMapDetail = (d: MapDetail): d is { mode: "context"; context: LocationContext } =>
  d.mode === "context";

export const isOpsMapDetail = (d: MapDetail): d is { mode: "ops"; context: OpsLocationStatus } =>
  d.mode === "ops";

export type InfoWindowDetail = {
  detail: MapDetail
  latLng: google.maps.LatLng
}

export type InfoWindowEntity<E extends LocatableEntity> = {
  entity: E
  latLng: google.maps.LatLng
}

export const MAP_DEFAULT_CENTER: google.maps.LatLngLiteral = {
  lat: 38.685,
  lng: -115.234
};

export const MAP_DEFAULT_BOUNDS: google.maps.LatLngBoundsLiteral = {
  south: 18.853694845822282,
  west: -139.12288925000001,
  north: 49.75245970922224,
  east: -63.53695175000002
};

export type MapProps = PropsWithChildren<GoogleMapProps>

export const Map = (props: MapProps) => {
  const {children, ...rest} = props;

  return (
    <GoogleMap {...rest}>
      {children}
    </GoogleMap>
  );
};

const GOOGLE_MAPS_LIBRARIES : Libraries = ["drawing", "geometry", "visualization"]

export type UseMapLoaderProps = Omit<UseLoadScriptOptions, 'id' | 'libraries'>

export type UseMapLoaderResult = {
  isLoaded: boolean;
  loadError: Error | undefined;
}

export const useMapLoader = (props: UseMapLoaderProps): UseMapLoaderResult => {
  return useJsApiLoader({
    ...props,
    id: "google-map-script",
    libraries: GOOGLE_MAPS_LIBRARIES,
  })
}



